import { useState } from 'react'
import { ErroreApi } from '../servizi/api.ts'
import { nomeVerifica, preparaPasskey, registraPasskey, segnaComputerRicordato } from '../servizi/accesso-ricordato.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Avviso } from './Pezzi.tsx'
import { Bottone } from './base.tsx'

type Opzioni = Awaited<ReturnType<typeof preparaPasskey>>

/** Le opzioni valgono cinque minuti sul server: oltre quattro se ne preparano altre. */
const VALIDITA_OPZIONI = 4 * 60_000

/**
 * Registra la passkey del computer con Windows Hello o Touch ID (531, decisioni di Davide del 2/11/2026). Lo
 * usano la proposta dopo l'accesso e il riquadro del profilo.
 *
 * Le opzioni si chiedono al clic e non all'apertura, come fa invece la web app dell'iPhone: chiederle
 * sostituisce il cookie del dispositivo ricordato, e su un computer quel cookie puo' essere di un collega
 * che l'ha ricordato prima. Basta vedere la proposta, quindi, non cambia niente. Chrome, Edge e Firefox
 * aprono Windows Hello o Touch ID anche dopo l'attesa delle opzioni; se il browser vuole il gesto senza
 * attese in mezzo (Safari), il primo tentativo non parte e il secondo clic usa le opzioni gia' pronte.
 */
export function useRicordaComputer(fatto: () => void) {
  const [opzioni, setOpzioni] = useState<{ o: Opzioni; dal: number } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function ricorda() {
    setErrore(null); setInCorso(true)
    let pronte = opzioni && Date.now() - opzioni.dal < VALIDITA_OPZIONI ? opzioni : null
    try {
      if (!pronte) {
        pronte = { o: await preparaPasskey('computer'), dal: Date.now() }
        setOpzioni(pronte)
      }
      await registraPasskey(pronte.o)
      setOpzioni(null)
      segnaComputerRicordato(true)
      fatto()
    } catch (e) {
      if (e instanceof ErroreApi) {
        // Il server ha rifiutato la passkey o la registrazione e' scaduta: si ricomincia da capo.
        setErrore(e.message); setOpzioni(null)
      } else {
        setErrore(`${nomeVerifica().replace(/^./, (c) => c.toUpperCase())} non ha confermato. Premi di nuovo «Ricordami»; `
          + 'se il computer non la offre, continua a entrare con la password.')
      }
    } finally {
      setInCorso(false)
    }
  }

  return { errore, inCorso, ricorda }
}

/**
 * «Ricordami su questo computer» (531): dopo l'accesso completo, in un browser di computer con una passkey
 * della piattaforma. Per 30 giorni dall'ultimo uso si rientra con Windows Hello o Touch ID, senza password
 * né codice del secondo fattore, e la sessione resta aperta 4 ore senza attività. «Non ora» la rimanda di 30
 * giorni in questo browser; si può sempre fare da «Il mio profilo».
 */
export function RicordaComputer() {
  const { aggiorna, chiudiPropostaComputer } = useSessione()
  const { errore, inCorso, ricorda } = useRicordaComputer(() => {
    chiudiPropostaComputer(false)
    void aggiorna()
  })

  return (
    <div className="resta-connesso" role="dialog" aria-labelledby="ricorda-computer-titolo">
      <h2 id="ricorda-computer-titolo">Ricordami su questo computer</h2>
      <p>
        <strong>Solo sul tuo computer, non su quelli condivisi.</strong> Per 30 giorni dall’ultimo uso entri con
        {' '}{nomeVerifica()}, senza password né codice del secondo fattore, e la sessione resta aperta 4 ore senza
        attività. Lo revochi da «Il mio profilo».
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="resta-connesso-azioni">
        <Bottone variante="primario" inCorso={inCorso} onClick={() => { void ricorda() }}>
          Ricordami
        </Bottone>
        <Bottone variante="discreto" onClick={() => chiudiPropostaComputer(true)}>Non ora</Bottone>
      </div>
    </div>
  )
}
